import { ReactNode } from 'react'
import { motion } from 'framer-motion'
import AgentPulse from './AgentPulse'
import KnowledgeConstellation from './KnowledgeConstellation'
import LiveStream from './LiveStream'
import PatternHeatmap from './PatternHeatmap'
import CoordinationScore from './CoordinationScore'

interface LayoutProps {
  children?: ReactNode
}

// Stagger animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1,
    }
  }
}

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
}

export default function Layout({ children }: LayoutProps) {
  return (
    <motion.div 
      className="min-h-screen bg-gray-950 p-4 md:p-6 lg:p-8"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {/* Header */}
      <motion.header 
        className="mb-6"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <motion.div variants={itemVariants}>
            <motion.h1 
              className="text-3xl md:text-4xl font-bold text-white tracking-tight"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              Cortex Nexus
            </motion.h1>
            <motion.p 
              className="text-gray-400 text-sm tracking-widest uppercase mt-1"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4, delay: 0.3 }}
            >
              Agent Intelligence Dashboard
            </motion.p>
          </motion.div>
          
          {/* Coordination Score - Top right */}
          <motion.div 
            className="w-full md:w-80"
            variants={itemVariants}
          >
            <CoordinationScore />
          </motion.div>
        </div>
      </motion.header>

      {/* Main grid layout */}
      <motion.div 
        className="grid grid-cols-1 lg:grid-cols-12 gap-6"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Left sidebar - Agent Pulse */}
        <motion.div 
          className="lg:col-span-3 order-2 lg:order-1"
          variants={itemVariants}
        >
          <motion.div 
            className="glass-panel rounded-xl p-6 flex flex-col items-center"
            whileHover={{ 
              boxShadow: '0 12px 40px rgba(0, 0, 0, 0.4), 0 0 30px rgba(0, 212, 255, 0.1)',
            }}
          >
            <h2 className="text-lg font-semibold text-white mb-4 self-start">Agent Pulse</h2>
            <AgentPulse />
          </motion.div>
        </motion.div>

        {/* Center - Knowledge Constellation */}
        <motion.div 
          className="lg:col-span-6 order-1 lg:order-2"
          variants={itemVariants}
        >
          <motion.div 
            className="glass-panel rounded-xl p-6"
            whileHover={{ 
              boxShadow: '0 12px 40px rgba(0, 0, 0, 0.4), 0 0 30px rgba(168, 85, 247, 0.1)',
            }}
          >
            <h2 className="text-lg font-semibold text-white mb-4">Knowledge Constellation</h2>
            <div className="w-full overflow-x-auto">
              <KnowledgeConstellation 
                width={800} 
                height={500} 
                similarityThreshold={0.7} 
              />
            </div>
          </motion.div>
        </motion.div>

        {/* Right sidebar - Live Stream */}
        <motion.div 
          className="lg:col-span-3 order-3"
          variants={itemVariants}
        >
          <LiveStream />
        </motion.div>

        {/* Bottom panel - Pattern Heatmap */}
        <motion.div 
          className="lg:col-span-12 order-4"
          variants={itemVariants}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <motion.div 
              className="lg:col-span-2"
              whileHover={{ scale: 1.01 }}
              transition={{ duration: 0.2 }}
            >
              <PatternHeatmap />
            </motion.div>
            
            {/* System Status - Enhanced glass card */}
            <motion.div 
              className="glass-panel rounded-xl p-6"
              variants={itemVariants}
              whileHover={{ 
                boxShadow: '0 12px 40px rgba(0, 0, 0, 0.4), 0 0 20px rgba(16, 185, 129, 0.1)',
              }}
            >
              <h3 className="text-lg font-semibold text-white mb-4">System Status</h3>
              <div className="space-y-4">
                <motion.div 
                  className="flex items-center justify-between"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.8 }}
                >
                  <span className="text-sm text-gray-400">API Status</span>
                  <motion.span 
                    className="flex items-center gap-2"
                    whileHover={{ scale: 1.05 }}
                  >
                    <motion.span 
                      className="w-2 h-2 rounded-full bg-emerald-500"
                      animate={{
                        boxShadow: [
                          '0 0 8px #10b981',
                          '0 0 16px #10b981',
                          '0 0 8px #10b981',
                        ]
                      }}
                      transition={{ duration: 2, repeat: Infinity }}
                    />
                    <span className="text-sm text-emerald-400">Online</span>
                  </motion.span>
                </motion.div>
                <motion.div 
                  className="flex items-center justify-between"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.9 }}
                >
                  <span className="text-sm text-gray-400">Database</span>
                  <motion.span 
                    className="flex items-center gap-2"
                    whileHover={{ scale: 1.05 }}
                  >
                    <motion.span 
                      className="w-2 h-2 rounded-full bg-emerald-500"
                      animate={{
                        boxShadow: [
                          '0 0 8px #10b981',
                          '0 0 16px #10b981',
                          '0 0 8px #10b981',
                        ]
                      }}
                      transition={{ duration: 2, repeat: Infinity, delay: 0.3 }}
                    />
                    <span className="text-sm text-emerald-400">Connected</span>
                  </motion.span>
                </motion.div>
                <motion.div 
                  className="flex items-center justify-between"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 1 }}
                >
                  <span className="text-sm text-gray-400">Last Update</span>
                  <span className="text-sm text-gray-300">
                    {new Date().toLocaleTimeString('en-US', { 
                      hour: '2-digit', 
                      minute: '2-digit' 
                    })}
                  </span>
                </motion.div>
              </div>
            </motion.div>

            {/* Quick Actions - Enhanced glass buttons */}
            <motion.div 
              className="glass-panel rounded-xl p-6"
              variants={itemVariants}
              whileHover={{ 
                boxShadow: '0 12px 40px rgba(0, 0, 0, 0.4), 0 0 20px rgba(0, 212, 255, 0.1)',
              }}
            >
              <h3 className="text-lg font-semibold text-white mb-4">Quick Actions</h3>
              <div className="space-y-2">
                <motion.button 
                  className="w-full px-4 py-2 bg-cyan-500/10 text-cyan-400 rounded-lg text-sm border border-cyan-500/30"
                  whileHover={{ 
                    backgroundColor: 'rgba(0, 212, 255, 0.2)',
                    boxShadow: '0 0 20px rgba(0, 212, 255, 0.3)',
                    scale: 1.02,
                  }}
                  whileTap={{ scale: 0.98 }}
                  transition={{ duration: 0.2 }}
                >
                  Refresh Data
                </motion.button>
                <motion.button 
                  className="w-full px-4 py-2 bg-purple-500/10 text-purple-400 rounded-lg text-sm border border-purple-500/30"
                  whileHover={{ 
                    backgroundColor: 'rgba(168, 85, 247, 0.2)',
                    boxShadow: '0 0 20px rgba(168, 85, 247, 0.3)',
                    scale: 1.02,
                  }}
                  whileTap={{ scale: 0.98 }}
                  transition={{ duration: 0.2 }}
                >
                  Export Report
                </motion.button>
                <motion.button 
                  className="w-full px-4 py-2 bg-emerald-500/10 text-emerald-400 rounded-lg text-sm border border-emerald-500/30"
                  whileHover={{ 
                    backgroundColor: 'rgba(16, 185, 129, 0.2)',
                    boxShadow: '0 0 20px rgba(16, 185, 129, 0.3)',
                    scale: 1.02,
                  }}
                  whileTap={{ scale: 0.98 }}
                  transition={{ duration: 0.2 }}
                >
                  View Logs
                </motion.button>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </motion.div>

      {/* Footer */}
      <motion.footer 
        className="mt-8 pt-6 border-t border-gray-800"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 1.2 }}
      >
        <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-gray-500">
          <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 1.3 }}
          >
            Cortex Nexus Dashboard v1.0.0
          </motion.div>
          <motion.div 
            className="flex gap-4"
            initial={{ opacity: 0, x: 10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 1.4 }}
          >
            <span>Real-time monitoring</span>
            <span>•</span>
            <span>Multi-agent coordination</span>
            <span>•</span>
            <span>Knowledge graph visualization</span>
          </motion.div>
        </div>
      </motion.footer>

      {children}
    </motion.div>
  )
}
